import { FC } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import { Box, Stack } from "@mui/material"
import { useMutationCreateSchoolGroupTransactions } from "api/reactQuery/mutations/schoolGroups"
import Dialog from "components/common/dialog/dialog"
import {
  transactionsDefaultValue,
  transactionsSchema,
} from "components/form/common/formPart/transactions/Transactions.config"
import { ITransactionsData } from "components/form/common/formPart/transactions/Transactions.types"
import {
  getCombinedTransactions,
  getIsNoTransactionAdded,
} from "components/form/common/formPart/transactions/Transactions.utils"
import { useCustomSnackbar } from "hooks/snackbar"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { IGroup } from "ts/interfaces/Group"
import { getErrorMessageFromTab } from "utils/api"

import ManualSection from "./schoolGroupTransactionSections/ManualSection"
import PresetSection from "./schoolGroupTransactionSections/PresetSection"

interface IProps {
  open: boolean
  group: IGroup
  onClose: () => void
}

const SendSchoolGroupTransactionDialog: FC<IProps> = ({
  open,
  group,
  onClose,
}) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()

  const methods = useForm<ITransactionsData>({
    resolver: yupResolver(transactionsSchema),
    defaultValues: transactionsDefaultValue,
  })

  const { mutate, isLoading } = useMutationCreateSchoolGroupTransactions({
    options: {
      onSuccess: () => {
        showSnackbar({ title: t("students.transactionsSentSuccessfully") })
        handleClose()
      },
      onError: (error) =>
        showSnackbar({
          title: t(getErrorMessageFromTab(error)),
          variant: "error",
        }),
    },
  })

  const handleClose = () => {
    methods.reset(transactionsDefaultValue)
    onClose()
  }

  const handleActionButtonClick = () => {
    const data = methods.getValues()

    if (getIsNoTransactionAdded(data)) {
      showSnackbar({
        title: t("students.selectAtLeaseOneTransaction"),
        variant: "info",
      })
      return
    }

    const allTransactions = getCombinedTransactions(data)
    if (allTransactions.length > 20) {
      showSnackbar({
        title: t("students.selectUpTo20Transactions"),
        variant: "info",
      })
      return
    }

    methods.handleSubmit(() =>
      mutate({ groupId: group.id, data: { transactions: allTransactions } })
    )()
  }

  return (
    <Dialog
      titleText={t("students.sendTransaction")}
      onActionButtonClick={handleActionButtonClick}
      isLoading={isLoading}
      open={open}
      onClose={handleClose}
      desktopMaxWidth="1040px"
      tabletMaxWidth="720px"
      actionAcceptButtonProps={{ disabled: isLoading }}
    >
      <Box width="100%" component="form">
        <FormProvider {...methods}>
          <Stack gap="16px">
            <PresetSection
              variant="bonus"
              defaultExpanded
              title={t("transaction.bonuses")}
              noElementsMessage={t("transaction.schoolDontHaveAnyBonusesYet")}
              tilesFieldName="bonuses"
              manualTitleFieldName="bonusTitle"
              manualValueFieldName="bonusValue"
              manualTitleLabel={t("transaction.bonusTitle")}
            />
            <PresetSection
              variant="fine"
              title={t("transaction.fines")}
              noElementsMessage={t("transaction.schoolDontHaveAnyFinesYet")}
              tilesFieldName="fines"
              manualTitleFieldName="fineTitle"
              manualValueFieldName="fineValue"
              manualTitleLabel={t("transaction.fineTitle")}
            />
            <ManualSection
              title={t("transaction.expenses")}
              titleFieldName="expenseTitle"
              valueFieldName="expenseValue"
              titleLabel={t("transaction.expenseTitle")}
              valueLabel={t("transaction.value")}
            />
            <ManualSection
              title={t("transaction.paychecks")}
              titleFieldName="paycheckTitle"
              valueFieldName="paycheckValue"
              titleLabel={t("transaction.paycheckTitle")}
              valueLabel={t("transaction.value")}
            />
          </Stack>
        </FormProvider>
      </Box>
    </Dialog>
  )
}

export default SendSchoolGroupTransactionDialog
